import React, { useState } from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { FiClock, FiAlertCircle, FiX } from 'react-icons/fi';

export default function MissedCheckoutModal({ record, docId, onDone }) {
  const [saving, setSaving] = useState(false);
  const [confirmAbsent, setConfirmAbsent] = useState(false);
  const [error, setError] = useState('');

  const date = record.date; // "YYYY-MM-DD"
  const checkInTime = record.checkInTime?.toDate();
  const checkInStr = checkInTime
    ? checkInTime.toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit' })
    : '—';
  const displayDate = new Date(date + 'T12:00:00').toLocaleDateString('ar-SA', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  });

  // Self-service checkout was removed on purpose: a forgotten checkout is counted as an absence,
  // and the only way to reclaim the day is a departure-time request the manager approves (shown as
  // a 24h alert on the dashboard). This writes the same absence marker the scheduled job uses.
  const handleMarkAbsent = async () => {
    setSaving(true);
    try {
      await updateDoc(doc(db, 'attendance', docId), {
        status: 'Absent',
        cancelled: true,
        absentReason: 'no_checkout',
        hoursVoided: true,
        absentMarkedAt: serverTimestamp(),
      });
      onDone();
    } catch (err) {
      console.error('[MissedCheckout] cancel failed:', err);
      setError('حدث خطأ، حاول مجدداً.');
    } finally {
      setSaving(false);
    }
  };

  /* ── shared styles ── */
  const card = {
    background: '#fff',
    borderRadius: 20,
    width: '100%',
    maxWidth: 440,
    overflow: 'hidden',
    boxShadow: '0 24px 64px rgba(0,0,0,0.30)',
    direction: 'rtl',
    fontFamily: 'Nunito, Segoe UI, sans-serif',
  };

  const btn = (bg, color, border) => ({
    width: '100%',
    padding: '13px',
    background: bg,
    color,
    border: border || 'none',
    borderRadius: 12,
    fontFamily: 'inherit',
    fontSize: 15,
    fontWeight: 800,
    cursor: saving ? 'not-allowed' : 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    opacity: saving ? 0.65 : 1,
    transition: 'opacity 0.2s',
    marginBottom: 10,
  });

  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: 'rgba(15,35,65,0.78)',
      backdropFilter: 'blur(7px)',
      WebkitBackdropFilter: 'blur(7px)',
      zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 20,
    }}>
      <div style={card}>

        {/* ── Orange header ── */}
        <div style={{
          background: 'linear-gradient(135deg, #f5a623 0%, #e8941a 100%)',
          padding: '20px 24px',
          display: 'flex', alignItems: 'center', gap: 14,
        }}>
          <div style={{
            width: 48, height: 48, borderRadius: '50%',
            background: 'rgba(255,255,255,0.22)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontSize: 24, flexShrink: 0,
          }}>
            <FiAlertCircle />
          </div>
          <div>
            <div style={{ color: '#fff', fontWeight: 900, fontSize: 17, lineHeight: 1.3 }}>
              نسيت تسجّل خروجك!
            </div>
            <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: 13, marginTop: 3 }}>
              {displayDate}
            </div>
          </div>
        </div>

        {/* ── Body ── */}
        <div style={{ padding: '22px 24px 20px' }}>

          {/* Check-in time info */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            background: '#f0f6ff', borderRadius: 12,
            padding: '11px 16px', marginBottom: 18,
            border: '1px solid #dbeafe',
          }}>
            <FiClock style={{ color: '#4a90e2', fontSize: 18, flexShrink: 0 }} />
            <span style={{ fontSize: 14, color: '#1e40af', fontWeight: 600 }}>
              سجّلت دخولك الساعة <strong>{checkInStr}</strong> ولم يُسجّل خروجك
            </span>
          </div>

          {!confirmAbsent ? (
            <>
              <p style={{ fontSize: 14, color: '#64748b', margin: '0 0 16px', lineHeight: 1.8 }}>
                لن يُحتسب هذا اليوم كدوام لأنك لم تُسجّل خروجك. لاحتساب دوامك، قدّم طلبًا للمدير
                بساعة مغادرتك من <strong>التنبيه في الصفحة الرئيسية</strong>.
              </p>

              {error && (
                <div style={{
                  background: '#fef2f2', border: '1px solid #fecaca',
                  borderRadius: 10, padding: '10px 14px', marginBottom: 14,
                  fontSize: 13, color: '#dc2626', fontWeight: 600,
                }}>
                  {error}
                </div>
              )}

              <button onClick={onDone} disabled={saving} style={btn(
                'linear-gradient(135deg, #4a90e2, #357abd)', '#fff'
              )}>
                حسناً، فهمت
              </button>

              <button onClick={() => setConfirmAbsent(true)} disabled={saving} style={btn(
                'transparent', '#e74c3c', '1.5px solid #fecaca'
              )}>
                <FiX size={16} />
                تأكيد الغياب الآن
              </button>
            </>
          ) : (
            <>
              <div style={{
                background: '#fef2f2', border: '1px solid #fecaca',
                borderRadius: 14, padding: '18px 16px',
                marginBottom: 18, textAlign: 'center',
              }}>
                <div style={{ fontSize: 15, color: '#dc2626', fontWeight: 900, marginBottom: 8 }}>
                  ⚠ تأكيد الغياب
                </div>
                <div style={{ fontSize: 13, color: '#991b1b', lineHeight: 1.7 }}>
                  سيتم إلغاء تسجيل حضورك ليوم <strong>{displayDate}</strong>
                  <br />ولن يُحتسب كيوم دوام.
                </div>
              </div>

              {error && (
                <div style={{
                  background: '#fef2f2', border: '1px solid #fecaca',
                  borderRadius: 10, padding: '10px 14px', marginBottom: 14,
                  fontSize: 13, color: '#dc2626', fontWeight: 600,
                }}>
                  {error}
                </div>
              )}

              <button onClick={handleMarkAbsent} disabled={saving} style={btn('#dc2626', '#fff')}>
                {saving ? 'جاري الحفظ...' : 'تأكيد — لم أحضر هذا اليوم'}
              </button>

              <button onClick={() => setConfirmAbsent(false)} disabled={saving} style={btn('#f1f5f9', '#64748b', '1.5px solid #e2e8f0')}>
                رجوع
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
